import {
  Component,
  ComponentWillLoad,
  Event,
  EventEmitter,
  h,
  Listen,
  Prop,
  State,
} from '@stencil/core';

import { Appearance } from '@entities/Appearance';
import { Design } from '@entities/Design';
import { DynamicFormValue } from '@entities/DynamicFormValue';
import { ValidateStatus } from '@entities/FieldPayload';
import { Question, QuestionSegment } from '@entities/Question';
import { QuestionResponse } from '@entities/QuestionResponse';
import { WidgetPosition } from '@entities/WidgetPosition';
import { createFocusTrap } from 'focus-trap';

@Component({
  tag: 'wog-modern-feedback',
})
export class ModernFeedback implements ComponentWillLoad {
  @Prop() questions: Question[];
  @Prop() position: WidgetPosition;
  @Prop() design: Design;
  @Prop() themeColor: string;
  @Prop() appearance: Appearance;
  @Prop() segment: QuestionSegment;
  @State() isValid = false;
  @State() registry: Record<string, QuestionResponse>;
  @State() requiredQuestions: string[] = [];
  @State() preparedQuestions: Question[] = [];
  @State() responses: QuestionResponse[];
  @Event() feedbackCancel: EventEmitter;
  @Event() feedbackSubmit: EventEmitter;

  private feedbackContainer: HTMLDivElement;
  private trap = createFocusTrap('', {});

  @Listen('keydown', { target: 'body' })
  handleKeyDown(ev: KeyboardEvent) {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      this.handleCancel();
    }
  }

  componentWillLoad(): Promise<void> | void {
    this.preparedQuestions = this.questions
      .filter(q => q.questionSegment === this.segment)
      .sort((a, b) => a.index - b.index);
    const { registry, requiredQuestions } = this.getRegistry(this.preparedQuestions);
    this.registry = registry;
    this.requiredQuestions = requiredQuestions;
  }

  componentDidLoad() {
    this.trap = createFocusTrap(this.feedbackContainer, {
      allowOutsideClick: true,
      clickOutsideDeactivates: true,
      escapeDeactivates: false,
      initialFocus: this.feedbackContainer,
      fallbackFocus: this.feedbackContainer,
      onPostActivate: () => {
        this.trapDeactivate();
        document.body.focus();
      },
    });
    this.trapActivate();
  }

  private trapActivate = () => {
    if (!this.trap.active) {
      this.trap.activate();
    }
  };

  private trapDeactivate = () => {
    if (this.trap.active) {
      this.trap.deactivate();
    }
  };

  private getRegistry(questions: Question[]) {
    const registry = {};
    let requiredQuestions: string[] = [];
    for (const question of questions) {
      const { hasOther, index, questionId, questionType, isRequired } = question;
      registry[questionId] = {
        hasOther,
        index,
        questionId,
        questionType,
        isRequired,
        isValid: ValidateStatus.None,
        value: null,
      };
      requiredQuestions = isRequired ? [...requiredQuestions, questionId] : requiredQuestions;
    }
    return {
      registry,
      requiredQuestions,
    };
  }

  private handleCancel = () => {
    this.feedbackCancel.emit();
  };

  private handleValidStatusChange = (event: CustomEvent<DynamicFormValue>) => {
    const { status, responses } = event.detail;
    this.isValid = status;
    this.responses = responses;
  };

  private handleSubmit = (event: Event): void => {
    event.preventDefault();
    if (this.isValid) {
      this.trapDeactivate();
      const values = this.responses.map(
        ({ index, questionId, questionType, value, displayedOptions }) =>
          displayedOptions
            ? {
                questionNumber: index,
                questionId,
                questionType,
                response: value,
                displayedOptions: displayedOptions,
              }
            : {
                questionNumber: index,
                questionId,
                questionType,
                response: value,
              },
      );
      this.feedbackSubmit.emit({ values });
    }
  };

  render() {
    const rootClass = {
      'wog--tab-view-container': true,
      'wog--flex': true,
      'wog--flex-col': true,
      'wog--modern-feedback-custom': true,
      'wog--mobile-full-position': true,
      'wog--position-left-side': this.position === WidgetPosition.MiddleLeft,
      'wog--position-right-side': this.position === WidgetPosition.MiddleRight,
      'wog--position-bottom-left': this.position === WidgetPosition.BottomLeft,
      'wog--position-bottom-right': this.position === WidgetPosition.BottomRight,
    };

    const submitButtonCx = {
      'wog--button': true,
      'wog--float-right': true,
      'wog--cursor-pointer': this.isValid,
      'wog--opacity-50': !this.isValid,
      'wog--cursor-not-allowed': !this.isValid,
    };

    const backgroundStyles = {
      backgroundColor: this.design !== Design.SmileyFace ? this.themeColor : '#333333',
    };
    const textStyles = {
      color: this.appearance === Appearance.Light ? 'white' : '#333333',
    };
    const formStyles = {
      width: 'inherit',
      minWidth: 'auto',
      minHeight: '40px',
    };

    return (
      <div
        class={rootClass}
        ref={el => (this.feedbackContainer = el as HTMLDivElement)}
        tabIndex={0}
        role="group"
        aria-roledescription="Sentiments widget popup"
        aria-labelledby="wogFeedbackHeading">
        <wog-close-button
          isWhite={this.appearance === Appearance.Light}
          onClose={this.handleCancel}
        />
        <div
          class="wog--flex wog--items-center wog--justify-center wog--p-4"
          style={backgroundStyles}>
          <h3
            id="wogFeedbackHeading"
            class="wog--text-foundation wog--base-title wog--text-lg wog--font-normal"
            style={textStyles}>
            Tell us more
          </h3>
        </div>
        <form
          novalidate
          aria-label="Add a feedback"
          name="wogSentimentsForm"
          class="wog--flex wog--h-full wog--flex-col wog--justify-between"
          style={formStyles}
          onSubmit={this.handleSubmit}>
          <div class="wog--overflow-y-auto wog--px-4 wog--pb-4 wog--text-left">
            <wog-dynamic-form
              onValidStatusChange={this.handleValidStatusChange}
              questions={this.preparedQuestions}
              registry={this.registry}
              requiredQuestions={this.requiredQuestions}
            />
          </div>
          <div class="wog--px-4 wog--py-2">
            <button
              tabIndex={0}
              type="submit"
              id="wogSubmitForm"
              disabled={!this.isValid}
              style={{ ...backgroundStyles, ...textStyles }}
              class={submitButtonCx}>
              SUBMIT
            </button>
          </div>
        </form>
      </div>
    );
  }
}
